import type {
  TranscriptQueryParams,
  StudentTranscriptRow,
  StudentProgressItem,
  TranscriptDownloadStatusResponse,
} from '../services/api';
import { ApiClient } from '../services/api';
import { withRetry } from './retry';

// The transcript API clamps per_page to 100; page math must use the same
// number or later pages are silently skipped and the export is incomplete.
const EXPORT_PAGE_SIZE = 100;
const FETCH_CONCURRENCY = 5;
// The backend download-status endpoint accepts up to 500 ids per call.
const STATUS_CHUNK_SIZE = 500;

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Student-level CSV mirroring the Transcripts table columns (SOPHIA-35098).
 */
export function transcriptStudentsToCSV(
  rows: Array<{ row: StudentTranscriptRow; reviewStatus: string }>
): string {
  const headers = ['Student', 'Email', 'Courses', 'Review Status', 'Latest Certified'];

  const body = rows.map(({ row, reviewStatus }) => [
    escapeCSV(`${row.student_last_name}, ${row.student_first_name}`),
    escapeCSV(row.student_email),
    String(row.course_count),
    escapeCSV(reviewStatus),
    formatDate(row.latest_certification_date),
  ]);

  return [headers.join(','), ...body.map((r) => r.join(','))].join('\n');
}

/**
 * Student-progress CSV for checkbox-selected rows (SOPHIA-35147).
 * Must stay byte-compatible with the server export (APIs/student
 * GET /students/export): BOM, same header, every field quoted,
 * newlines collapsed, sorted by last name then first name.
 */
export function studentProgressToCSV(rows: StudentProgressItem[]): string {
  const esc = (value: string | number | null | undefined): string =>
    `"${String(value ?? '')
      .replace(/"/g, '""')
      .replace(/[\r\n]+/g, ' ')}"`;

  const header = 'Student,Email,Courses,Certified,In Progress,Last Activity,Date of Birth';
  const sorted = [...rows].sort(
    (a, b) => a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name)
  );
  const body = sorted.map((s) =>
    [
      esc(`${s.last_name}, ${s.first_name}`),
      esc(s.email),
      esc(s.course_count),
      esc(s.completed_count),
      esc(s.in_progress_count),
      esc(s.last_activity ? s.last_activity.slice(0, 10) : ''),
      // Already YYYY-MM-DD from the API; never parsed, so no timezone shift
      esc(s.birthdate),
    ].join(',')
  );
  return '\uFEFF' + [header, ...body].join('\n') + '\n';
}

export function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Fetch every student row matching the filters from /transcripts/by-student.
 */
export async function fetchAllTranscriptStudents(
  api: ApiClient,
  partnerId: number,
  params: TranscriptQueryParams,
  onProgress?: (fetched: number, total?: number) => void
): Promise<StudentTranscriptRow[]> {
  const first = await withRetry(() =>
    api.getTranscriptsByStudent(partnerId, {
      ...params,
      page: 1,
      per_page: EXPORT_PAGE_SIZE,
    })
  );
  let all: StudentTranscriptRow[] = [...first.items];
  onProgress?.(all.length, first.total);
  const totalPages = Math.ceil(first.total / EXPORT_PAGE_SIZE);

  // Page 1 told us the total, so fetch the rest concurrently. Promise.all
  // preserves page order within a batch and batches run in order, so the
  // combined list keeps the server's sort. Each request retries transient
  // rate-limit (429) / CORS failures so a large fan-out doesn't fail the set
  for (let start = 2; start <= totalPages; start += FETCH_CONCURRENCY) {
    const batch: Promise<{ items: StudentTranscriptRow[] }>[] = [];
    for (let p = start; p < start + FETCH_CONCURRENCY && p <= totalPages; p++) {
      const page = p;
      batch.push(
        withRetry(() =>
          api.getTranscriptsByStudent(partnerId, {
            ...params,
            page,
            per_page: EXPORT_PAGE_SIZE,
          })
        )
      );
    }
    const results = await Promise.all(batch);
    for (const r of results) all = all.concat(r.items);
    onProgress?.(all.length, first.total);
  }

  return all;
}

/**
 * Fetch download status for an arbitrary number of transcript ids,
 * chunked to the backend's per-call limit.
 */
export async function fetchDownloadStatusForIds(
  backendApi: ApiClient,
  partnerId: string,
  transcriptIds: number[]
): Promise<{
  my: TranscriptDownloadStatusResponse['my_downloads'];
  org: TranscriptDownloadStatusResponse['org_downloads'];
}> {
  const my: TranscriptDownloadStatusResponse['my_downloads'] = {};
  const org: TranscriptDownloadStatusResponse['org_downloads'] = {};
  const chunks: number[][] = [];
  for (let i = 0; i < transcriptIds.length; i += STATUS_CHUNK_SIZE) {
    chunks.push(transcriptIds.slice(i, i + STATUS_CHUNK_SIZE));
  }
  for (let i = 0; i < chunks.length; i += FETCH_CONCURRENCY) {
    const results = await Promise.all(
      chunks
        .slice(i, i + FETCH_CONCURRENCY)
        .map((chunk) => withRetry(() => backendApi.getTranscriptDownloadStatus(partnerId, chunk)))
    );
    for (const status of results) {
      Object.assign(my, status.my_downloads);
      Object.assign(org, status.org_downloads);
    }
  }
  return { my, org };
}
